/**
 * PATH: packages/ui/src/components/email/entityLabel.ts
 * FILE_ID: UI-EMAIL-ENTITYLABEL-001
 * PURPOSE: Pure, display-only shortener for the job / board tags shown on
 *          email cards. Turns "#5735 — Watpac Construction Pty Ltd" into
 *          "Watpac Construction" so the workflow tag reads as a NAME, not a
 *          database row. Callers keep passing the RAW label to the chip's
 *          `title` (tooltip), so the full reference is never lost — this
 *          only trims the *visible* text.
 * WHY:     Operator ("tag clutter in the cards"): long "#NNNN — Company Pty
 *          Ltd" job names were width hogs that pushed the rest of the chip
 *          strip off-screen and made every card read as clutter. A short,
 *          human label fixes readability universally with no per-job manual
 *          rename. (A user-settable nickname can layer on top later — this
 *          is the zero-config baseline.)
 * FLOW:
 *   ┌──────────┐   ┌─────────────────────┐   ┌──────────────────────┐   ┌────────────┐
 *   │ rawLabel │ → │ strip "#1234 — " job│ → │ strip "Pty Ltd" / etc│ → │ short label│
 *   │          │   │ number prefix       │   │ company-type suffix  │   │ (or raw)   │
 *   └──────────┘   └─────────────────────┘   └──────────────────────┘   └────────────┘
 */

// Leading job-number prefix: optional '#', 2+ digits, then a hyphen / en
// dash / em dash / colon separator with at least a trailing space. We
// require the space AFTER the separator so we never bite into address
// names like "12-34 Smith St". e.g. "#5735 — ", "5735 - ", "#42: ".
const JOB_NUMBER_PREFIX = /^#?\d{2,}\s*[–—:-]\s+/

// Trailing company-type tokens, anchored to the end, case-insensitive,
// tolerating a trailing period. Longest forms (Pty Ltd / Proprietary
// Limited) come first so the whole suffix is stripped in one pass. The
// leading `\s+` means the token must be preceded by a real name — so a
// bare "Pty Ltd" with nothing in front is left untouched (then the empty
// guard returns the original anyway).
const COMPANY_SUFFIX =
  /\s+(?:pty\.?\s*ltd\.?|proprietary\s+limited|pty\.?|ltd\.?|limited|incorporated|inc\.?|llc|l\.l\.c\.|corp\.?)\s*$/i

/**
 * Shorten a job / board label for display on a chip. Pure I→O.
 *
 * @param raw the full label (e.g. "#5735 — Watpac Construction Pty Ltd")
 * @returns a trimmed short label ("Watpac Construction"); the original
 *          (trimmed) when shortening would empty it; '' for null/undefined.
 */
export function shortenEntityLabel(raw?: string | null): string {
  const original = (raw ?? '').trim()
  if (!original) return ''

  let out = original
  const deprefixed = out.replace(JOB_NUMBER_PREFIX, '')
  // Only treat the leading number as a job-id prefix when what follows is a
  // WORD (a name). Guards against mangling address-range names like
  // "1784 - 1790 Main Road" where the digits are part of the name itself.
  if (deprefixed !== out && /^[A-Za-z]/.test(deprefixed)) {
    out = deprefixed
  }

  out = out.replace(COMPANY_SUFFIX, '').trim()

  // Never collapse to empty (e.g. the label was just "#5735" or "Pty Ltd").
  return out || original
}
